import { queryOptions, useQuery } from '@tanstack/react-query';

import { getApiClient } from '@cast/api-client';
import { QueryRefetchInterval } from '@cast/shared-types';
import { Cluster, ClusterResponse } from '@cast/types';

import { isAnywhereCluster } from './clusterTypeGuards';
import { enhanceCluster } from './enhanceCluster';

export const EXTERNAL_CLUSTER_QUERY_KEY = 'external-cluster';

export const getClusterQueryKey = (params: { id: string }) =>
  [EXTERNAL_CLUSTER_QUERY_KEY, params.id] as const;

const getClusterQueryOptions = ({
  id,
  refetchInterval = 20_000,
}: {
  id: string | undefined;
  refetchInterval?: QueryRefetchInterval<Cluster>;
}) =>
  queryOptions({
    queryKey: getClusterQueryKey({ id: id! }),
    queryFn: async () => {
      const apiClient = getApiClient();
      const { data: cluster } = await apiClient.clusters.getCluster({
        clusterId: id!,
      });
      let clusterData = cluster as ClusterResponse;

      // "Temporary" solution for showing cluster region for CAST Anywhere clusters
      // Remove as part of UI-4028
      if (isAnywhereCluster(clusterData as Cluster)) {
        const { data: nodeData } = await apiClient.clusters.getNodes({
          clusterId: id!,
        });
        const filteredNodes = nodeData.items?.filter(
          (node) => !node.labels?.['omni.cast.ai/edge-id']
        );
        const region =
          filteredNodes?.[0]?.labels?.['topology.kubernetes.io/region'];

        clusterData = {
          ...clusterData,
          region: {
            name: region ?? '',
            displayName: region ?? '',
          },
        };
      }

      return enhanceCluster(clusterData);
    },
    refetchInterval,
    enabled: !!id,
  });

export const useClusterQuery = (
  id?: string,
  refetchInterval: number | false = 20_000
) => {
  // Demo cluster data is handled by middleware - it returns fixtures when in demo mode
  const {
    data: cluster,
    error,
    isLoading,
    refetch,
  } = useQuery(getClusterQueryOptions({ id, refetchInterval }));

  return {
    cluster,
    error,
    isLoading,
    refetch,
  };
};
